import { FocusableContext } from 'react-aria/private/interactions/useFocusable';
import type { ReactNode } from 'react';

/**
 * Putting a `Button` or any other focusable inside a `Tooltip` breaks its
 * positioning: the tooltip renders at the top-left of the viewport instead of
 * next to its trigger. Wrap the tooltip's content in this component to fix
 * that.
 *
 * Why: every react-aria focusable registers its DOM node in
 * `FocusableContext`, and React context crosses the tooltip's portal boundary.
 * A focusable in the content therefore overwrites the trigger's node, and the
 * tooltip positions itself against the wrong element. Nulling the context for
 * the tooltip's subtree keeps the trigger's registration intact; it also stops
 * content focusables from inheriting the trigger's hover and press handlers.
 *
 * Wrap a tooltip's content whenever it contains anything focusable:
 *
 * ```tsx
 * <TooltipTrigger>
 *   <Button>7 components</Button>
 *   <Tooltip>
 *     <TooltipContentShield>
 *       <ButtonIcon icon={<CopyIcon />} aria-label="Copy list" />
 *     </TooltipContentShield>
 *   </Tooltip>
 * </TooltipTrigger>
 * ```
 */
export function TooltipContentShield({ children }: { children: ReactNode }) {
  return (
    <FocusableContext.Provider value={null}>
      {children}
    </FocusableContext.Provider>
  );
}
